/* eslint-disable prettier/prettier */
import * as React from "react";

import {
  Grid,
  Link,
  TextField,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  Radio,
  Stack,
  Typography,
  Button,
  Menu,
  MenuItem,
  Snackbar,
  Box,
} from "@mui/material";
import {
  formatToUsDateFromUTCNew,
  formatTimeWithAMPM,
} from "../utils/DateHelper";
import { PrivilegesContext } from "../PrivilegesContext";
import NoteIcon from "@mui/icons-material/Note";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

// components
import AddPatientList from "../patients/AddPatientList";
import EditPatientList from "../patients/EditPatientList";
import PatientNotesShow from "../modals/PatientNotesShow";
import { Link as RouterLink, useHistory } from "react-router-dom";
import SortComponent from "../common/SortComponent";
import usePagination from "../hooks/usePagination";
import InviteToAppButton from "./InviteToAppButton";
import PatientInfoCard from "./PatientInfoCard";

// helper imports
import { getHeaders } from "../utils/HeaderHelper";
import { sanitizeAdvName } from "../utils/DemographicValidationHelper";


// app setting imports
import {
  AuthenticationContext,
  ImagesContext,
  NewPatientModalContext
} from "../Context";
import { capitalize } from "../utils/StringHelper";
import QuestionnaireQr from "../modals/QuestionnaireQr";
import DatePickerWithLabel from "../common/DatePickerWithLabel";
import PatientsAdvancedFilters from "./PatientsAdvancedFilters";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import BadgeList from "../common/BadgeList";
import { checkPrivileges } from "../utils/PrivilegesHelper";
import { useFlash } from "../FlashContext";

const offlineIcon = require("../../images/offlineIcon.svg");

interface ChipData {
  key: number;
  label: string;
  type: string;
}

interface Props {
  csrfToken: string;
  menu_track_src: string;
  sort_plain_src: string;
  sort_ascending_src: string;
  sort_descending_src: string;
  selectedPatient: any;
  setSelectedPatient: any;
  setShowEditForm: any;
  setTempSelectedPatient: any;
  unsaveChanges: boolean;
  setUnsavedModalOpen: any;
  renderingKey: number;
  patient_reports_icon: string;
}

const SEARCH_MRN_MAX_LENGTH = 20;
const ADV_FIRST_NAME_MAX_LENGTH = 50;
const ADV_LAST_NAME_MAX_LENGTH = 50;

const normalize = (value?: string) => value?.trim() ?? "";

const PatientTable: React.FC<Props> = (props: any) => {
  // authenticationContext and chat context and other contexts
  const authenticationSetting = React.useContext(AuthenticationContext);
  const images = React.useContext(ImagesContext);
  const { showToast } = useFlash();

  const [fullPatients, setFullPatients] = React.useState<any>([]);
  const [paginatedList, setPaginatedList] = React.useState<any>(null);
  const [sortObject, setSortObject] = React.useState<any>({
    field: "last_name",
    direction: "descending",
  });
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const perPage = 20;
  const [patients, setPatients] = React.useState<any>(null);
  const [patientLists, setPatientLists] = React.useState<any>([]);
  const [selectedPatientList, setSelectedPatientList] =
    React.useState<any>(null); //this is an object
  const [advFirstName, setAdvFirstName] = React.useState<string>("");
  const [advLastName, setAdvLastName] = React.useState<string>("");
  const [advDateOfBirth, setAdvDateOfBirth] = React.useState<string>("");
  const [searchMrnNumber, setSearchMrnNumber] = React.useState<string>("");
  const [advFollowUpDate, setAdvFollowUpDate] = React.useState<string>("");
  const [openAddPatientListModal, setOpenAddPatientListModal] =
    React.useState<boolean>(false);
  const [openEditPatientListModal, setOpenEditPatientListModal] =
    React.useState<boolean>(false);
  const [editListSelection, setEditListSelection] = React.useState<any>("");

  // patient notes modal controllers
  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const [noteForId, setNoteForId] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [loadingPatientInfo, setLoadingPatientInfo] =
    React.useState<boolean>(false);

  const userPrivileges = React.useContext(PrivilegesContext);
  const afterFetch = React.useRef(false);
  const fetchTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  const [qrModalOpen, setQrModalOpen] = React.useState(false);

  const [recentHistory, setRecentHistory] = React.useState([]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [anchorElFilters, setAnchorElFilters] =
    React.useState<null | HTMLElement>(null);
  const [selectAllOrganizationsChecked, setSelectAllOrganizationsChecked] =
    React.useState(
      checkPrivileges(userPrivileges, "Global Patient Search") ? true : false
    );
  const [badgeData, setBadgeData] = React.useState<ChipData[]>([]);
  const [organizationsCount, setOrganizationsCount] = React.useState(0);

  const openRecentPatientMenu = Boolean(anchorEl);

  const handleRecentHistoryClick = async () => {
    try {
      const response = await fetch(`/data_fetching/retrieve_patient_history`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.status === 404) {
        const data = await response.json();
        showToast({
          title: "Error",
          text: data.message,
          type: "error",
        });
        setRecentHistory([]);
      } else if (response.ok) {
        const data = await response.json();
        setRecentHistory(data);
      } else {
        const errorData = await response.json();
        showToast({
          title: "Error",
          text: errorData.message || "An unexpected error occurred.",
          type: "error",
        });
        setRecentHistory([]);
      }
    } catch (error) {
      showToast({
        title: "Error", 
        text: "An unexpected error occurred",
        type: "error",
      });
      setRecentHistory([]);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    handleRecentHistoryClick();
  };

  const handlePatientsAdvancedFiltersMenu = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setAnchorElFilters(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  React.useEffect(() => {
    getPatients();
  }, [props.renderingKey]);

  React.useEffect(() => {
    if (fullPatients && afterFetch.current) {
      sortList();
    }
  }, [sortObject]);

  React.useEffect(() => {
    if (!afterFetch.current) {
      return;
    }
    updatePagination();
  }, [patients, currentPage]);

  React.useEffect(() => {
    if (!afterFetch.current) {
      return;
    }
    clearTimeout(fetchTimerRef.current);
    fetchTimerRef.current = setTimeout(() => {
      advancedSearch();
    }, 500);
  }, [
    selectedPatientList,
    advFirstName,
    advLastName,
    searchMrnNumber,
    advDateOfBirth,
  ]);

  React.useEffect(() => {
    if (openEditPatientListModal == false && afterFetch.current) {
      getPatients(); //refreshes patient lists
      advancedSearch();
    }
  }, [openEditPatientListModal]);

  React.useEffect(() => {
    if (openAddPatientListModal == false && afterFetch.current) {
      getPatients(); //refreshes patient lists
    }
  }, [openAddPatientListModal]);

  React.useEffect(() => {
    if (!afterFetch.current) {
      return;
    }
    // check if list was deleted
    let filtered =
      patientLists?.filter((list) => list.id == selectedPatientList?.id) || [];
    if (filtered.length == 0) {
      setSelectedPatientList(null);
    }
  }, [patientLists]);

  const getPatients = () => {
    fetch(
      `/data_fetching/patient_index?patient_list=${
        selectedPatientList ? selectedPatientList?.id : ""
      }`,
      {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      }
    )
      .then((result) => result.json())
      .then((result) => {
        if (typeof result.error !== "undefined") {
          console.log(result.error);
        } else {
          setFullPatients(result.patients);
          setPatientLists(result.patient_lists);
          setOrganizationsCount(result.organizations_count);
          afterFetch.current = true;
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const advancedSearch = () => {
    props.setSelectedPatient(null);

    if (!hasFilters()) {
      setPatients(null);
      return;
    }

    setLoading(true);

    const firstName = normalize(advFirstName);
    const lastName = normalize(advLastName);
    const mrn = normalize(searchMrnNumber);

    fetch(
      `/data_fetching/patient_index?first_name=${encodeURIComponent(firstName)}&last_name=${encodeURIComponent(
        lastName
      )}&mrn_number=${encodeURIComponent(mrn)}&date_of_birth=${advDateOfBirth}&patient_list=${
        selectedPatientList?.id ?? ""
      }&follow_up_date=${advFollowUpDate}&all_organizations=${selectAllOrganizationsChecked}`,
      {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      }
    )
      .then((res) => res.json())
      .then((result) => {
        setLoading(false);
        if (result.error) {
          console.log(result.error);
        } else {
          setPatients(result.patients);
          setPaginatedList(paginateList(result.patients));
          setCurrentPage(1);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
      });
  };

  const setFirstPatientSelection = (user) => {
    const url_string = window.location.href;
    const url = new URL(url_string);
    const patient_id = url.searchParams.get("patient_id");
    const isEdit =
      url.searchParams.get("action") &&
      url.searchParams.get("action") === "edit";
    if (patient_id) {
      fetch(`/data_fetching/edit_my_info/${patient_id}`, {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      })
        .then((result) => result.json())
        .then((result) => {
          if (typeof result.error !== "undefined") {
            console.log(result.error);
          } else {
            props.setSelectedPatient(result);
            props.setTempSelectedPatient(result);
            if (isEdit) {
              props.setShowEditForm(true);
            }
          }
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      props.setSelectedPatient(user);
      props.setTempSelectedPatient(user);
    }
  };

  const selectPage = (page) => {
    setCurrentPage(page);
  };
  const { pageCount, pageLinks } = usePagination(
    patients,
    perPage,
    currentPage,
    selectPage
  );

  const sortList = () => {
    const patientList = getSortedAndSearchedList();
    if (afterFetch.current == false) {
      setFirstPatientSelection(patientList[0]);
    }
    setPatients(patientList);
    setCurrentPage(1);
  };

  const getSortedAndSearchedList = () => {
    let patientList = [...patients];
    patientList.sort((a, b) => (a.id > b.id ? 1 : -1));

    if (sortObject.field == "last_sign_in_at") {
      patientList.sort((a, b) =>
        new Date(a["last_sign_in_at"]) > new Date(b["last_sign_in_at"]) ? 1 : -1
      );
    }

    if (sortObject.field == "formatted_follow_up") {
      patientList.sort((a, b) =>
        new Date(a["formatted_follow_up"]) > new Date(b["formatted_follow_up"])
          ? 1
          : -1
      );
    }

    if (sortObject.field == "last_name") {
      patientList.sort((a, b) =>
        a.last_name?.toLowerCase() > b.last_name?.toLowerCase() ? 1 : -1
      );
    }

    if (sortObject.direction == "descending") {
      patientList.reverse();
    }

    return patientList;
  };

  const setSortOrder = (field: string, direction: string) => {
    setSortObject({ field, direction });
  };

  const paginateList = (list) => {
    if (!list) {
      return null;
    }
    if (list.length == 0) {
      return [];
    } else {
      let patientList = list;
      if (currentPage == 1) {
        return patientList.slice(0, perPage);
      } else {
        let offsetIndex = perPage * (currentPage - 1);
        return patientList.slice(offsetIndex, offsetIndex + perPage);
      }
    }
  };

  const updatePagination = () => {
    setPaginatedList(paginateList(patients));
  };

  const clearAdvancedSearch = () => {
    setAdvDateOfBirth("");
    setAdvFollowUpDate("");
    setAdvLastName("");
    setAdvFirstName("");
  };

  const history = useHistory();

  const handlePatientSelection = (user) => {
    if (props.unsaveChanges) {
      props.setTempSelectedPatient(user);
      props.setUnsavedModalOpen(true);
    } else {
      setLoadingPatientInfo(true);
      fetch(`/data_fetching/patient_history_creation`, {
        method: "POST",
        headers: getHeaders(authenticationSetting.csrfToken),
        body: JSON.stringify({
          patient_id: user.id,
          last_name: user.last_name,
          first_name: user.first_name,
        }),
      })
        .then((result) => result.json())
        .then((result) => {
          setLoadingPatientInfo(false);
          if (typeof result.error !== "undefined") {
            console.log(result.error);
            showToast({
              title: "Error",
              text: result.error,
              type: "error",
            });
          } else {
            props.setSelectedPatient(user);
            props.setTempSelectedPatient(user);
            props.setShowEditForm(false);
          }
        })
        .catch((error) => {
          showToast({
            title: "Error",
            text: error.message || "An error occurred",
            type: "error",
          });
          setLoadingPatientInfo(false);
          console.log(error);
        });
    }
  };

  const handlePatientNotesShow = (patientId) => {
    setNoteForId(patientId);
  };

  const handleLimitedInput =
    (
      setter: React.Dispatch<React.SetStateAction<string>>,
      maxLength: number
    ) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value;

      if (value.length <= maxLength) {
        setter(value);
      }
    };

  const handleSearchMrnChange = handleLimitedInput(
    setSearchMrnNumber,
    SEARCH_MRN_MAX_LENGTH
  );

  const handleAdvFirstNameChange = handleLimitedInput(
    setAdvFirstName,
    ADV_FIRST_NAME_MAX_LENGTH
  );

  const handleAdvLastNameChange = handleLimitedInput(
    setAdvLastName,
    ADV_LAST_NAME_MAX_LENGTH
  );

  React.useEffect(() => {
    if (noteForId) {
      setModalOpen(true);
    }
  }, [noteForId]);

  React.useEffect(() => {
    if (!modalOpen) {
      setNoteForId("");
    }
  }, [modalOpen]);

  const hasFilters = () => {
    return [advFirstName, advLastName, advDateOfBirth, searchMrnNumber].some(
      (f) => f && f.trim() !== "" && f.length > 1
    );
  };

  const handleDeleteBadge = (badgeToDelete) => {
    setSelectAllOrganizationsChecked(false);
  };

  React.useEffect(() => {
    advancedSearch();
    if (selectAllOrganizationsChecked) {
      const initialBadges: ChipData[] = [
        {
          key: 1,
          label: "All Organizations Search",
          type: "All Organizations Search",
        },
      ];
      setBadgeData(initialBadges);
    } else {
      setBadgeData([]);
    }
  }, [selectAllOrganizationsChecked]);

  return (
    <Grid item xs={12} className="patient-index-container ">
      <Grid container className="patient-search-container">
        <Grid container>
          <Grid item xs={12}>
            <p className="search-form-label">PATIENT SEARCH</p>
          </Grid>
        </Grid>
        <Grid container justifyContent="flex-start" spacing={2}>
          <Grid item xs={2}>
            <TextField
              id="search"
              className="textInput plainLabel"
              label="Last Name"
              value={advLastName}
              onChange={handleAdvLastNameChange}
              variant="filled"
              size="small"
              inputProps={{ maxLength: ADV_LAST_NAME_MAX_LENGTH }}
              InputProps={{
                disableUnderline: true,
                style: { paddingBottom: "4px", height: "49px" },
                sx: { "& input": { padding: "21px 12px 4px" } },
              }}
              InputLabelProps={{
                sx: { '&[data-shrink="true"]': { marginTop: "-3px" } },
              }}
              onKeyPress={(e) => {
                if (e.key === "Enter") advancedSearch();
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              id="search"
              className="textInput plainLabel"
              label="First Name"
              value={advFirstName}
              onChange={handleAdvFirstNameChange}
              variant="filled"
              size="small"
              inputProps={{ maxLength: ADV_FIRST_NAME_MAX_LENGTH }}
              InputProps={{
                disableUnderline: true,
                style: { paddingBottom: "4px", height: "49px" },
                sx: { "& input": { padding: "21px 12px 4px" } },
              }}
              InputLabelProps={{
                sx: { '&[data-shrink="true"]': { marginTop: "-3px" } },
              }}
              onKeyPress={(e) => {
                if (e.key === "Enter") advancedSearch();
              }}
            />
          </Grid>
          <Grid item xs={2}>
           <TextField
              id="search"
              className="textInput plainLabel"
              label="MRN"
              value={searchMrnNumber}
              onChange={handleSearchMrnChange}
              variant="filled"
              size="small"
              inputProps={{
                maxLength: 20,
                inputMode: "numeric",
                pattern: "^[a-zA-Z0-9-]*$",
              }}
              InputProps={{
                disableUnderline: true,
                style: { paddingBottom: "4px", height: "49px" },
                sx: { "& input": { padding: "21px 12px 4px" } },
              }}
              InputLabelProps={{
                sx: { '&[data-shrink="true"]': { marginTop: "-3px" } },
              }}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  advancedSearch();
                }
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <div style={{ marginTop: "10px" }}>
              <DatePickerWithLabel
                label="Date of Birth"
                date={advDateOfBirth}
                setDate={setAdvDateOfBirth}
                initialDate=""
                height="49px"
                marginTop="3px"
                fontSize={14}
              />
            </div>
          </Grid>
          <Grid item xs={2} sx={{ marginLeft: "auto", alignSelf: "center" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleMenuClick}
              startIcon={<AccessTimeIcon />}
              style={{
                width: "100%",
                height: "50px",
              }}
            >
              Recent Patients
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={openRecentPatientMenu}
              onClose={handleClose}
            >
              <Box sx={{ bgcolor: "white", padding: 2 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#1E1E1E",
                    fontSize: "16px",
                    fontWeight: 700,
                  }}
                >
                  Recent Patients
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#A29D9B",
                  }}
                >
                  {recentHistory.length} Patients
                </Typography>
              </Box>
              <Box sx={{ bgcolor: "#F8F8F8", padding: "1rem" }}>
                {recentHistory.map((item) => (
                  <Link
                    key={item.id}
                    component={RouterLink}
                    to={`/patient_reports/${item.patient_id}/action_center`}
                    underline="none"
                    color="#5868BF"
                  >
                    <MenuItem
                      onClick={handleClose}
                      style={{ padding: "10px", width: "100%" }}
                    >
                      <Typography
                        sx={{
                          marginRight: 1,
                          font: "12px QuicksandMedium",
                          color: "black",
                        }}
                      >
                        {formatTimeWithAMPM(item.created_at)}
                      </Typography>
                      <Typography
                        sx={{
                          color: "#707070",
                          textDecoration: "underline",
                          font: "12px QuicksandMedium",
                        }}
                      >
                        {item.patient_name}
                      </Typography>
                    </MenuItem>
                  </Link>
                ))}
              </Box>
            </Menu>
          </Grid>
        </Grid>
      </Grid>

      {props.selectedPatient && (
        <Grid
          container
          className="panel-container"
          style={{ marginTop: "15px" }}
        >
          <Grid container className="panel-show-container">
            <Grid container className="panel-information-container">
              <Grid container direction="row">
                <Grid
                  item
                  xs={12}
                  className="box-header"
                  style={{ padding: "10px" }}
                >
                  <Stack
                    direction={"row"}
                    paddingX={3}
                    paddingY={1}
                    alignItems={"center"}
                    display={"flex"}
                    justifyContent={"space-between"}
                  >
                    <Grid
                      item
                      xs={3}
                      display={"flex"}
                      direction={"row"}
                      alignItems={"center"}
                    >
                      <p className="immunization-result">Patient Information</p>
                    </Grid>
                    <Grid
                      xs={3}
                      display={"flex"}
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"flex-end"}
                    >
                      {props.selectedPatient?.user_creation_type == "invited" &&
                      props.selectedPatient?.invitation_accepted === true ? (
                        <Grid item>
                          <p
                            className="row"
                            style={{
                              background: "none",
                              gap: "5px",
                              margin: 0,
                            }}
                          >
                            <img
                              src={images.check_mark_icon}
                              alt="Edit Patient"
                              className="action-icon-image"
                              style={{ width: "31.466px", maxHeight: "32px" }}
                            />
                            <span className="app-user-text disable-pointer">
                              Online User
                            </span>
                          </p>
                        </Grid>
                      ) : (
                        <>
                          <Grid item>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginRight: "16px",
                              }}
                            >
                              <img
                                src={offlineIcon}
                                alt="Offline User"
                                style={{ width: "25px", marginRight: "5px" }}
                              />
                              <Typography
                                variant="body1"
                                style={{
                                  color: "var(--light-grey, #A29D9B)",
                                  fontWeight: "bold",
                                  fontSize: "14px",
                                }}
                              >
                                Offline User
                              </Typography>
                            </div>
                          </Grid>
                          <Grid item>
                            {props.selectedPatient && (
                              <InviteToAppButton
                                patientId={props.selectedPatient.id}
                              />
                            )}
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Stack>
                </Grid>
              </Grid>

              <Grid container className="general-patient-info-container">
                <Grid
                  item
                  xs={12}
                  style={{
                    display: "flex",
                    width: "100%",
                    flexDirection: "column",
                  }}
                >
                  {loadingPatientInfo ? (
                    <div className="loading-container">
                      <img
                        src={images.new_large_spinner_src}
                        alt="Loading spinner"
                        style={{ width: "80px", marginTop: "3rem" }}
                      />
                      <span
                        className="loading-text"
                        style={{ marginBottom: "3rem" }}
                      >
                        Loading...
                      </span>
                    </div>
                  ) : (
                    <PatientInfoCard
                      patient_reports_icon={props.patient_reports_icon}
                      selectedPatient={props.selectedPatient}
                      setQrModalOpen={setQrModalOpen}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

      <Grid container className="panel-container" style={{ marginTop: "15px" }}>
        <Grid container className="panel-show-container">
          <Grid container className="panel-information-container">
            <Grid container direction="row">
              <Grid
                item
                xs={12}
                className="box-header"
                style={{ padding: "10px" }}
              >
                <Stack
                  direction={"row"}
                  paddingX={3}
                  paddingY={1}
                  alignItems={"center"}
                  display={"flex"}
                  justifyContent="space-between"
                >
                  <Grid
                    item
                    xs={3}
                    display={"flex"}
                    direction={"row"}
                    alignItems={"center"}
                  >
                    <p className="immunization-result">Search Results:</p>
                    {patients && patients.length > 0 && (
                      <p className="immunization-count-container badge">
                        {patients.length}
                      </p>
                    )}
                  </Grid>
                  {checkPrivileges(userPrivileges, "Global Patient Search") && (
                    <div
                      className="advanced-search-container"
                      onClick={handlePatientsAdvancedFiltersMenu}
                    >
                      <FilterListIcon sx={{ width: 20, height: 20 }} />
                      <p className="advanced-search-text">Advanced Search</p>
                      {anchorElFilters ? (
                        <ArrowDropUp sx={{ width: 24, height: 24 }} />
                      ) : (
                        <ArrowDropDownIcon sx={{ width: 24, height: 24 }} />
                      )}
                    </div>
                  )}

                  {checkPrivileges(userPrivileges, "Global Patient Search") && (
                    <PatientsAdvancedFilters
                      anchorEl={anchorElFilters}
                      setAnchorEl={setAnchorElFilters}
                      selectAllOrganizationsChecked={
                        selectAllOrganizationsChecked
                      }
                      setSelectAllOrganizationsChecked={
                        setSelectAllOrganizationsChecked
                      }
                      patientsCount={patients?.length}
                      organizationsCount={organizationsCount}
                    />
                  )}
                </Stack>
              </Grid>
            </Grid>

            <Grid container className="general-patient-info-container">
              <Grid
                item
                xs={12}
                style={{
                  display: "flex",
                  height: "calc(100vh - 360px)",
                  width: "100%",
                  flexDirection: "column",
                }}
              >
                {loading ? (
                  <div className="loading-container">
                    <img
                      src={images.new_large_spinner_src}
                      alt="Loading spinner"
                    />
                    <span className="loading-text">Loading...</span>
                    <p>
                      We are loading your patients search results.
                      <br />
                      This might take a few minutes. Please do not leave this
                      page.
                    </p>
                  </div>
                ) : (
                  <>
                    {badgeData?.length > 0 && paginatedList?.length > 0 && (
                      <>
                        <div
                          className="badge-container"
                          style={{ width: "auto" }}
                        >
                          <BadgeList
                            badgeData={badgeData}
                            setBadgeData={setBadgeData}
                            handleDeleteBadge={handleDeleteBadge}
                            background="none"
                          />
                        </div>
                      </>
                    )}
                    <TableContainer
                      component={Paper}
                      style={{ height: loading ? "100%" : "auto" }}
                    >
                      <Table stickyHeader className="no-border-table">
                        <TableHead className="table-header-box">
                          <TableRow>
                            <TableCell
                              style={{ width: "30px" }}
                              className="override-padding"
                            ></TableCell>
                            <SortComponent
                              column_name="last_name"
                              column_title={`Patients ${
                                patients?.length > 0
                                  ? "(" + patients?.length + ")"
                                  : ""
                              }`}
                              sortObject={sortObject}
                              setSortOrder={setSortOrder}
                              className="patient-table table-header name-header nowrap-header"
                              sx={{
                                fontFamily: "QuicksandMedium",
                              }}
                              style={{ width: "25%" }}
                            />
                            {checkPrivileges(
                              userPrivileges,
                              "Global Patient Search"
                            ) && (
                              <TableCell
                                className="patient-table table-header name-header nowrap-header"
                                sx={{ fontFamily: "QuicksandMedium" }}
                                style={{ cursor: "pointer", width: "25%" }}
                              >
                                <div className="row">
                                  Organization Association(s)
                                </div>
                              </TableCell>
                            )}
                            <TableCell
                              className="patient-table table-header name-header nowrap-header"
                              sx={{ fontFamily: "QuicksandMedium" }}
                              style={{ cursor: "pointer", width: "25%" }}
                            >
                              <div className="row">Date of Birth</div>
                            </TableCell>
                            <TableCell
                              className="patient-table table-header name-header nowrap-header"
                              sx={{ fontFamily: "QuicksandMedium" }}
                              style={{ cursor: "pointer" }}
                            >
                              <div className="row">Sex</div>
                            </TableCell>
                            <TableCell
                              align="left"
                              className="table-header nowrap-header"
                              style={{ width: "70px" }}
                            >
                              <strong style={{ fontFamily: "QuicksandMedium" }}>
                                Notes
                              </strong>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedList &&
                          hasFilters() &&
                          paginatedList.length > 0 ? (
                            paginatedList.map((user, index) => (
                              <TableRow
                                key={index}
                                className={
                                  index % 2 == 0 ? "rowEven" : "rowEven"
                                }
                              >
                                <TableCell
                                  style={{ padding: "0 !important" }}
                                  className="override-padding"
                                >
                                  <Radio
                                    onChange={() => {
                                      handlePatientSelection(user);
                                    }}
                                    checked={
                                      props.selectedPatient?.id == user.id
                                    }
                                    name="radio-buttons"
                                    size="small"
                                    style={{ float: "right" }}
                                  />
                                </TableCell>
                                <TableCell
                                  size="small"
                                  scope="row"
                                  align="left"
                                  className="tableCell"
                                >
                                  {user.name_reversed}
                                </TableCell>
                                {checkPrivileges(
                                  userPrivileges,
                                  "Global Patient Search"
                                ) && (
                                  <TableCell
                                    size="small"
                                    scope="row"
                                    align="left"
                                    className="tableCell"
                                  >
                                    {user.associated_organizations?.join(", ")}
                                  </TableCell>
                                )}
                                <TableCell
                                  size="small"
                                  scope="row"
                                  align="left"
                                  className="tableCell"
                                >
                                  {user.date_of_birth
                                    ? formatToUsDateFromUTCNew(
                                        user.date_of_birth
                                      )
                                    : "N/A"}
                                </TableCell>
                                <TableCell
                                  size="small"
                                  scope="row"
                                  align="left"
                                  className="tableCell"
                                >
                                  {capitalize(user.gender)}
                                </TableCell>
                                <TableCell
                                  size="small"
                                  scope="row"
                                  align="left"
                                  className="tableCell"
                                >
                                  <Link
                                    onClick={() =>
                                      handlePatientNotesShow(user.id)
                                    }
                                  >
                                    <NoteIcon style={{ color: "#A29D9B" }} />
                                  </Link>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={8} align="center">
                                {afterFetch.current &&
                                hasFilters() &&
                                paginatedList ? (
                                  <p>No users found.</p>
                                ) : (
                                  <p>
                                    To search for a patient, enter criteria in
                                    the fields above
                                  </p>
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {patients && patients.length > 0 && (
                      <div className="pagerLinks" style={{ paddingTop: 16 }}>
                        <Link
                          onClick={() => {
                            selectPage(
                              currentPage > 1 ? currentPage - 1 : currentPage
                            );
                          }}
                          className="linkDark"
                        >
                          <ArrowBackIosIcon
                            sx={{ width: 16, height: 16, color: "#4A4442" }}
                          />
                        </Link>
                        {pageLinks}
                        <Link
                          onClick={() => {
                            selectPage(
                              currentPage < pageCount
                                ? currentPage + 1
                                : currentPage
                            );
                          }}
                          className="linkDark"
                        >
                          <ArrowForwardIosIcon
                            sx={{ width: 16, height: 16, color: "#4A4442" }}
                          />
                        </Link>
                      </div>
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <AddPatientList
        openModal={openAddPatientListModal}
        setOpenModal={setOpenAddPatientListModal}
      ></AddPatientList>
      <EditPatientList
        selectedPatientList={editListSelection}
        openModal={openEditPatientListModal}
        setOpenModal={setOpenEditPatientListModal}
      ></EditPatientList>
      {noteForId && (
        <PatientNotesShow
          patient_id={noteForId}
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
        />
      )}
      {qrModalOpen && (
        <QuestionnaireQr
          modalOpen={qrModalOpen}
          setModalOpen={setQrModalOpen}
          patientId={props.selectedPatient.id}
        />
      )}
    </Grid>
  );
};

export default PatientTable;
